import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { DataService } from './data.service';

interface SessionTokenSet {
  userId?: string | number | null;
  accessToken?: string | null;
  refreshToken?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class AuthTokenStoreService {
  private readonly userIdCookieName = 'djkn445fk45jk:dfd553dfgf';
  private readonly accessTokenCookieName = '78Hu78h8:383673:esd838d7s6s7w';
  private readonly refreshTokenCookieName = '78Hu78h8:383673:748388388hh43';
  private readonly tokenCookieExpiryMs = 24 * 60 * 60 * 1000;
  private readonly authCookieNames = [
    this.userIdCookieName,
    this.accessTokenCookieName,
    this.refreshTokenCookieName,
  ];

  private sessionUserId: string | null = null;
  private sessionAccessToken: string | null = null;
  private sessionRefreshToken: string | null = null;

  constructor(
    private readonly cookieService: CookieService,
    private readonly dataService: DataService
  ) {}

  storeSessionTokens(tokens: SessionTokenSet, persistCookies = true): void {
    const userId = this.resolveUserId(tokens.userId);
    const accessToken = this.normalizeToken(tokens.accessToken);
    const refreshToken = this.normalizeToken(tokens.refreshToken);

    if (userId) {
      this.sessionUserId = userId;
    }

    if (accessToken) {
      this.sessionAccessToken = accessToken;
    }

    if (refreshToken) {
      this.sessionRefreshToken = refreshToken;
    }

    if (!persistCookies) {
      return;
    }

    const cookieUserId = userId ?? this.resolveUserId();
    if (cookieUserId) {
      this.cookieService.set(this.userIdCookieName, cookieUserId, this.cookieOptions());
    }

    if (accessToken && cookieUserId) {
      this.cookieService.set(
        this.accessTokenCookieName,
        this.encryptToken(accessToken, cookieUserId),
        this.cookieOptions()
      );
    }

    if (refreshToken && cookieUserId) {
      this.cookieService.set(
        this.refreshTokenCookieName,
        this.encryptToken(refreshToken, cookieUserId),
        this.cookieOptions()
      );
    }
  }

  getUserId(): string | null {
    const currentUserId = this.readStoredUserId();
    if (!currentUserId) {
      this.clear();
      return null;
    }

    if (this.sessionUserId !== currentUserId) {
      this.sessionUserId = currentUserId;
      this.sessionAccessToken = null;
      this.sessionRefreshToken = null;
    }

    return currentUserId;
  }

  getAccessToken(): string | null {
    const userId = this.getUserId();
    if (!userId) {
      return null;
    }

    const decrypted = this.decryptCookie(this.accessTokenCookieName, userId);
    if (decrypted) {
      this.sessionAccessToken = decrypted;
    }

    return decrypted;
  }

  getRefreshToken(): string | null {
    const userId = this.getUserId();
    if (!userId) {
      return null;
    }

    const decrypted = this.decryptCookie(this.refreshTokenCookieName, userId);
    if (decrypted) {
      this.sessionRefreshToken = decrypted;
    }

    return decrypted;
  }

  clear(): void {
    this.sessionUserId = null;
    this.sessionAccessToken = null;
    this.sessionRefreshToken = null;
  }

  clearAuthCookies(): void {
    this.authCookieNames.forEach((cookieName) => {
      this.cookieService.delete(cookieName, '/');
      this.cookieService.delete(cookieName, '/', this.currentHostname());
      this.cookieService.delete(cookieName);
    });
  }

  private cookieOptions(): {
    path: string;
    secure: boolean;
    sameSite: 'Strict' | 'Lax';
    expires: Date;
  } {
    const isSecureContext =
      typeof window !== 'undefined' && window.location.protocol === 'https:';

    return {
      path: '/',
      secure: isSecureContext,
      sameSite: isSecureContext ? 'Strict' : 'Lax',
      expires: new Date(Date.now() + this.tokenCookieExpiryMs),
    };
  }

  private encryptToken(token: string, userId: string): string {
    return this.dataService.encryptToken(token, userId);
  }

  private decryptCookie(cookieName: string, userId: string | null): string | null {
    const token = this.cookieService.get(cookieName);
    if (!token || !userId) {
      return null;
    }

    try {
      const decrypted = this.dataService.decryptToken(token, userId);
      if (decrypted) {
        return decrypted;
      }

      this.handleInvalidAuthCookie();
      return null;
    } catch {
      this.handleInvalidAuthCookie();
      return null;
    }
  }

  private normalizeToken(value: string | null | undefined): string | null {
    const normalized = `${value ?? ''}`.trim();
    return normalized ? normalized : null;
  }

  private resolveUserId(value?: string | number | null): string | null {
    if (value === null || value === undefined) {
      return null;
    }

    const normalized = `${value}`.trim();
    return normalized ? normalized : null;
  }

  private readStoredUserId(): string | null {
    return this.resolveUserId(
      this.cookieService.get(this.userIdCookieName) || localStorage.getItem('userID')
    );
  }

  private currentHostname(): string {
    if (typeof window === 'undefined') {
      return '';
    }

    return window.location.hostname;
  }

  private handleInvalidAuthCookie(): void {
    this.clear();
    this.clearAuthCookies();

    if (typeof window !== 'undefined') {
      localStorage.removeItem('userID');
    }
  }
}
